import { useMutation } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DeleteWithConfirmation } from "components/DeleteWithConfirmation";
import { FullScreenDocumentPreview } from "components/Document/shared";
import { GetShippingDocumentQuery } from "generated-graphql/graphql";
import { ReactNode, useState } from "react";

import { DELETE_WASTE_SHIPPING_DOCUMENT } from "../ShippingDocuments/api";
import { EditShippingDocumentDialog } from "../ShippingDocuments/EditShippingDocumentDialog";
import {
  formatShippingDocumentDate,
  NO_VALUE_PLACEHOLDER,
  SHIPPING_DOCUMENT_KIND_LABELS,
} from "../ShippingDocuments/shippingDocumentDisplay";
import { ShippingDocumentLinesDataGrid } from "../ShippingDocuments/ShippingDocumentLinesDataGrid";
import { SHIPPING_PAPERWORK_QUERY } from "./api";
import { SectionCard } from "./sections/SectionCard";
import { SectionHeader } from "./sections/SectionHeader";
import { FlowArrow, PARTY_CARD_INSET, ShippingDocumentParties } from "./ShippingDocumentParties";

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;

function FieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ px: PARTY_CARD_INSET, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

function DetailHeaderRow({
  document,
  onEdit,
  onDelete,
  deleting,
}: {
  document: Document;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  deleting: boolean;
}) {
  return (
    // The rule runs the full width of the drawer, so the padding sits inside
    // this Box rather than on a parent — same shape as StickyManifestHeader,
    // which is what a manifest's title bar renders.
    <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <Typography variant="h5">{document.documentNumber ?? "Shipping document"}</Typography>
          <Chip label={SHIPPING_DOCUMENT_KIND_LABELS[document.kind]} size="small" />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Tooltip title="Edit shipping document">
            <IconButton aria-label="Edit shipping document" size="small" onClick={onEdit}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <DeleteWithConfirmation
            tooltip="Delete shipping document"
            header="Delete this shipping document?"
            resourceType="shipping document"
            resourceName={document.documentNumber ?? document.id}
            msg="This cannot be undone."
            loading={deleting}
            onConfirm={onDelete}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * The source document's title, as a link only when there is a file to open.
 * `FullScreenDocumentPreview` mounts on `downloadLink`, so linking on the
 * presence of the record alone gave a clickable title that opened nothing and
 * said nothing about why.
 */
function SourceDocumentValue({
  document,
  onPreview,
}: {
  document: Document;
  onPreview: () => void;
}) {
  const sourceDocument = document.sourceDocument;
  if (!sourceDocument) return <>{NO_VALUE_PLACEHOLDER}</>;
  if (!sourceDocument.downloadLink) {
    return (
      <Tooltip title="This document has no file to preview">
        <span>{sourceDocument.title}</span>
      </Tooltip>
    );
  }
  return (
    <Link component="button" onClick={onPreview}>
      {sourceDocument.title}
    </Link>
  );
}

function DocumentFieldsCard({
  document,
  onPreview,
}: {
  document: Document;
  onPreview: () => void;
}) {
  return (
    <SectionCard>
      <SectionHeader title="Details" />
      {/*
        Mirrors ShippingDocumentParties's row shape (same Stack direction,
        spacing, flex:1 items, and FlowArrow-width spacers) so these three
        fields land directly above the three party cards below. The party
        row's arrows shift when a card is missing, so this only lines up
        pixel-for-pixel in the three-card case; with fewer cards it still
        reads as three even fields above a shorter row.
      */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <FieldValue label="Ship date">{formatShippingDocumentDate(document.shipDate)}</FieldValue>
        <FlowArrow invisible />
        <FieldValue label="Created">{formatShippingDocumentDate(document.createdAt)}</FieldValue>
        <FlowArrow invisible />
        <FieldValue label="Source document">
          <SourceDocumentValue document={document} onPreview={onPreview} />
        </FieldValue>
      </Stack>
    </SectionCard>
  );
}

/**
 * The detail body for one shipping document: header actions, the field grid,
 * and the lines table. The caller resolves the document and owns the shell —
 * `ShippingDocumentDrawer` is the only consumer, and this component takes no
 * route params of its own.
 */
export function ShippingDocumentDetailContent({
  tenantId,
  document,
  onDeleted,
}: {
  tenantId: string;
  document: Document;
  /** Called after a successful delete, so the drawer can close itself. */
  onDeleted: () => void;
}) {
  const alerts = useAlerts();
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // The merged paperwork list is the only surface that renders these rows, so
  // it is the only query worth refetching.
  const [deleteDocument, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPPING_DOCUMENT, {
    refetchQueries: [SHIPPING_PAPERWORK_QUERY],
  });

  const handleDelete = async () => {
    try {
      await deleteDocument({ variables: { id: document.id } });
      alerts.success("Shipping document deleted");
      onDeleted();
    } catch (error) {
      alerts.error(
        "Could not delete the shipping document",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  return (
    <>
      <DetailHeaderRow
        document={document}
        onEdit={() => setEditOpen(true)}
        onDelete={handleDelete}
        deleting={deleting}
      />

      <Stack spacing={3} sx={{ px: 3, py: 2 }}>
        <DocumentFieldsCard document={document} onPreview={() => setPreviewOpen(true)} />

        <SectionCard>
          <SectionHeader title="Parties" />
          <ShippingDocumentParties document={document} />
        </SectionCard>

        <SectionCard>
          <SectionHeader title="Lines" />
          <ShippingDocumentLinesDataGrid tenantId={tenantId} shippingDocumentId={document.id} />
        </SectionCard>

        {editOpen && (
          <EditShippingDocumentDialog
            open
            onClose={() => setEditOpen(false)}
            document={document}
            onSaved={() => setEditOpen(false)}
          />
        )}

        {document.sourceDocument?.downloadLink && (
          <FullScreenDocumentPreview
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fileUrl={document.sourceDocument.downloadLink}
            fileExtension={document.sourceDocument.fileExtension ?? undefined}
            title={document.sourceDocument.title ?? "Document Preview"}
          />
        )}
      </Stack>
    </>
  );
}
